"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

// Carrossel da agenda. A rolagem é nativa (scroll-snap): no celular o dedo arrasta
// de verdade e o próximo card aparece pela borda, avisando que dá para deslizar.
// O avanço automático (5 s) é a própria barra de progresso: quando a animação dela
// termina, a página avança. Pausar = pausar a animação, então o tempo restante
// continua de onde parou. Pausa com mouse em cima, foco do teclado, toque (volta
// sozinho depois de um tempo parado), fora da tela, aba oculta ou pelo botão.
// Com "reduzir movimento" o CSS global zera as animações: sem autoplay.

export type CarouselShow = {
  id: number;
  date: string;
  city: string;
  uf: string;
  venue: string | null;
  ticket_url: string | null;
};

const INTERVAL = 5000;
const TOUCH_RESUME = 8000;

const fmtDay = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  timeZone: "UTC",
});
const fmtMonth = new Intl.DateTimeFormat("pt-BR", {
  month: "short",
  timeZone: "UTC",
});
const fmtMonthLong = new Intl.DateTimeFormat("pt-BR", {
  month: "long",
  timeZone: "UTC",
});
const fmtWeekday = new Intl.DateTimeFormat("pt-BR", {
  weekday: "long",
  timeZone: "UTC",
});

const toDate = (iso: string) => new Date(`${iso}T12:00:00Z`);
const clean = (s: string) => s.replace(".", "");
const pad = (n: number) => String(n).padStart(2, "0");
const weekday = (d: Date) => {
  const w = fmtWeekday.format(d);
  return w[0].toUpperCase() + w.slice(1);
}; // "Sexta-feira"

// "Hoje"/"Amanhã" só no navegador: a página é estática (ISR) e o HTML pode ser de ontem
const noop = () => () => {};
const todayBR = () =>
  new Date(Date.now() - 3 * 3600e3).toISOString().slice(0, 10);

function Chevron({ dir }: { dir: "left" | "right" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      className="size-5"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={dir === "left" ? "M15 18l-6-6 6-6" : "M9 6l6 6-6 6"} />
    </svg>
  );
}

export function ShowsCarousel({
  shows,
  bookingHref,
}: {
  shows: CarouselShow[];
  bookingHref: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLUListElement>(null);
  const touchTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const [first, setFirst] = useState(0); // índice do 1º card visível
  const [perView, setPerView] = useState(1);
  const [tick, setTick] = useState(0); // reinicia a barra a cada avanço/navegação
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [touch, setTouch] = useState(false);
  const [visible, setVisible] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const today = useSyncExternalStore(noop, todayBR, () => null);

  const playing =
    !hover && !focus && !touch && visible && !userPaused && !expanded;

  // Meses para os atalhos ("Out", "Nov"…), com o índice do 1º show de cada um
  const months = useMemo(() => {
    const out: { key: string; label: string; long: string; index: number }[] =
      [];
    const multiYear = new Set(shows.map((s) => s.date.slice(0, 4))).size > 1;
    shows.forEach((s, index) => {
      const key = s.date.slice(0, 7);
      if (out.at(-1)?.key === key) return;
      const d = toDate(s.date);
      const label =
        clean(fmtMonth.format(d)) + (multiYear ? ` ${s.date.slice(2, 4)}` : "");
      out.push({
        key,
        label,
        long: `${fmtMonthLong.format(d)} de ${s.date.slice(0, 4)}`,
        index,
      });
    });
    return out;
  }, [shows]);
  const currentMonth = shows[first]?.date.slice(0, 7);

  /** Largura de um card + o espaço entre eles. */
  const step = useCallback(() => {
    const track = trackRef.current;
    const card = track?.children[0] as HTMLElement | undefined;
    if (!track || !card) return 1;
    return (
      card.offsetWidth + (parseFloat(getComputedStyle(track).columnGap) || 0)
    );
  }, []);

  const measure = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const s = step();
    setFirst(Math.min(shows.length - 1, Math.round(track.scrollLeft / s)));
    // N cards ocupam N larguras + (N-1) espaços: soma um espaço antes de dividir
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    setPerView(Math.max(1, Math.floor((track.clientWidth + gap + 1) / s)));
  }, [shows.length, step]);

  const goTo = useCallback(
    (index: number) => {
      const track = trackRef.current;
      if (!track) return;
      track.scrollTo({ left: index * step(), behavior: "smooth" });
      setTick((t) => t + 1);
    },
    [step],
  );

  const atEnd = () => {
    const t = trackRef.current;
    return !t || t.scrollLeft + t.clientWidth >= t.scrollWidth - 4;
  };
  const next = () => (atEnd() ? goTo(0) : goTo(first + perView));
  const prev = () =>
    first <= 0
      ? goTo(Math.max(0, shows.length - perView))
      : goTo(Math.max(0, first - perView));

  useEffect(() => {
    // O ResizeObserver dispara já ao observar: faz a medição inicial
    const track = trackRef.current;
    const root = rootRef.current;
    if (!track || !root) return;
    const ro = new ResizeObserver(measure);
    ro.observe(track);
    const io = new IntersectionObserver(
      ([e]) => setVisible(e.isIntersecting && !document.hidden),
      { threshold: 0.35 },
    );
    io.observe(root);
    const onVis = () => document.hidden && setVisible(false);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      ro.disconnect();
      io.disconnect();
      document.removeEventListener("visibilitychange", onVis);
      clearTimeout(touchTimer.current);
    };
  }, [measure]);

  // Toque: pausa enquanto a pessoa mexe e retoma depois de um tempo sem interação
  const onTouch = () => {
    setTouch(true);
    clearTimeout(touchTimer.current);
    touchTimer.current = setTimeout(() => {
      setTouch(false);
      setTick((t) => t + 1);
    }, TOUCH_RESUME);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    if (e.key === "ArrowRight") next();
    else prev();
  };

  const last = Math.min(shows.length, first + perView);
  const btn =
    "grid size-11 place-items-center rounded-full border border-champanhe/30 text-champanhe transition hover:border-champanhe hover:bg-champanhe hover:text-noite disabled:opacity-30";

  return (
    <div
      ref={rootRef}
      role="region"
      aria-roledescription="carrossel"
      aria-label="Agenda de shows"
      data-state={playing ? "playing" : "paused"}
      onPointerEnter={(e) => e.pointerType === "mouse" && setHover(true)}
      onPointerLeave={(e) => e.pointerType === "mouse" && setHover(false)}
      onFocus={(e) => e.target.matches(":focus-visible") && setFocus(true)} // só teclado: toque/clique não trava o autoplay
      onBlur={(e) =>
        !e.currentTarget.contains(e.relatedTarget) && setFocus(false)
      }
    >
      {/* Barra de controles: meses (atalho), contador, pausar e setas */}
      <div className="mb-5 flex flex-wrap items-center justify-center gap-4 md:justify-between">
        <div
          className="flex flex-wrap justify-center gap-2"
          role="group"
          aria-label="Ir para o mês"
        >
          {months.map((m) => {
            const active = !expanded && m.key === currentMonth;
            return (
              <button
                key={m.key}
                type="button"
                onClick={() =>
                  expanded
                    ? document
                        .getElementById(`show-${shows[m.index].id}`)
                        ?.scrollIntoView({
                          behavior: "smooth",
                          block: "center",
                        })
                    : goTo(m.index)
                }
                aria-label={`Ver shows de ${m.long}`}
                aria-pressed={active}
                className={`rounded-full border px-4 py-2 text-xs font-medium uppercase tracking-[0.2em] transition ${
                  active
                    ? "border-champanhe bg-champanhe text-noite"
                    : "border-champanhe/25 text-areia/70 hover:border-champanhe/60 hover:text-areia"
                }`}
              >
                {m.label}
              </button>
            );
          })}
        </div>

        {expanded ? (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-xs font-medium uppercase tracking-[0.25em] text-champanhe underline decoration-champanhe/40 underline-offset-4 transition hover:decoration-champanhe"
          >
            Voltar ao carrossel
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <p
              className="mr-2 text-sm tabular-nums text-areia/60"
              aria-live="polite"
            >
              <span className="text-areia">
                {perView > 1
                  ? `${pad(first + 1)}–${pad(last)}`
                  : pad(first + 1)}
              </span>{" "}
              de {pad(shows.length)}
            </p>
            <button
              type="button"
              onClick={() => setUserPaused((p) => !p)}
              aria-label={
                userPaused
                  ? "Retomar rotação automática"
                  : "Pausar rotação automática"
              }
              className={btn}
            >
              {userPaused ? (
                <svg
                  viewBox="0 0 24 24"
                  aria-hidden
                  className="size-4"
                  fill="currentColor"
                >
                  <path d="M7 4.5v15l12-7.5z" />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  aria-hidden
                  className="size-4"
                  fill="currentColor"
                >
                  <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
                </svg>
              )}
            </button>
            <button
              type="button"
              onClick={prev}
              aria-label="Shows anteriores"
              className={btn}
            >
              <Chevron dir="left" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Próximos shows"
              className={btn}
            >
              <Chevron dir="right" />
            </button>
          </div>
        )}
      </div>

      <ul
        ref={trackRef}
        tabIndex={expanded ? -1 : 0}
        aria-label={
          expanded ? "Todas as datas" : "Use as setas do teclado para navegar"
        }
        onScroll={measure}
        onKeyDown={expanded ? undefined : onKey}
        onTouchStart={onTouch}
        onTouchMove={onTouch}
        onWheel={(e) =>
          Math.abs(e.deltaX) > Math.abs(e.deltaY) && setTick((t) => t + 1)
        }
        className={
          expanded
            ? "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
            : "scrollbar-none -mx-5 flex snap-x snap-mandatory scroll-px-5 gap-4 overflow-x-auto overscroll-x-contain px-5 pb-1 outline-none md:mx-0 md:scroll-px-0 md:gap-5 md:px-0"
        }
      >
        {shows.map((s, i) => {
          const d = toDate(s.date);
          const badge =
            today === s.date
              ? "Hoje"
              : today && toDate(today).getTime() + 864e5 === d.getTime()
                ? "Amanhã"
                : null;
          return (
            <li
              key={s.id}
              id={`show-${s.id}`}
              aria-roledescription="slide"
              aria-label={`${i + 1} de ${shows.length}`}
              className={
                expanded
                  ? ""
                  : "shrink-0 basis-[78%] snap-start sm:basis-[calc((100%-1rem)/2)] lg:basis-[calc((100%-3.75rem)/4)]"
              }
            >
              {expanded ? (
                // Lista completa: linha compacta (data | cidade e local | botão), cabe muita data na tela
                <article className="flex h-full items-center gap-4 rounded-xl border border-champanhe/15 bg-vinho/30 p-4">
                  <p className="w-12 shrink-0 text-center">
                    <span className="block font-display text-4xl leading-[0.85] text-champanhe">
                      {fmtDay.format(d)}
                    </span>
                    <span className="mt-1 block text-[0.65rem] font-semibold uppercase tracking-[0.25em] text-areia">
                      {clean(fmtMonth.format(d))}
                    </span>
                  </p>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-base leading-tight text-areia">
                      {s.city} <span className="text-areia/50">· {s.uf}</span>
                    </h3>
                    <p className="mt-0.5 line-clamp-2 text-xs text-areia/55">
                      {badge && (
                        <span className="mr-1.5 font-semibold uppercase tracking-[0.15em] text-rubi">
                          {badge}
                        </span>
                      )}
                      {weekday(d)}
                      {s.venue && ` · ${s.venue}`}
                    </p>
                  </div>
                  <a
                    href={s.ticket_url ?? bookingHref}
                    target="_blank"
                    rel="noopener"
                    data-track="contato"
                    data-label={`${s.ticket_url ? "Ingressos" : "Contato"} ${s.city}`}
                    className="shrink-0 rounded-full border border-champanhe/40 px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.2em] text-champanhe transition hover:bg-champanhe hover:text-noite"
                  >
                    {s.ticket_url ? "Ingressos" : "Contato"}
                  </a>
                </article>
              ) : (
                <article className="group relative flex h-full flex-col rounded-2xl border border-champanhe/15 bg-gradient-to-b from-vinho/70 to-noite p-6 transition hover:border-champanhe/45">
                  {badge && (
                    <span className="absolute right-5 top-5 rounded-full bg-rubi px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-areia">
                      {badge}
                    </span>
                  )}
                  <p className="flex items-end gap-3">
                    <span className="font-display text-6xl leading-[0.8] text-champanhe">
                      {fmtDay.format(d)}
                    </span>
                    <span className="pb-0.5">
                      <span className="block text-sm font-semibold uppercase tracking-[0.25em] text-areia">
                        {clean(fmtMonth.format(d))}
                      </span>
                      <span className="block text-xs text-areia/50">
                        {weekday(d)}
                      </span>
                    </span>
                  </p>
                  <h3 className="mt-6 text-xl leading-tight text-areia">
                    {s.city} <span className="text-areia/50">· {s.uf}</span>
                  </h3>
                  <p className="mb-6 mt-1.5 line-clamp-2 min-h-10 text-sm text-areia/55">
                    {s.venue ?? "Local a confirmar"}
                  </p>
                  <a
                    href={s.ticket_url ?? bookingHref}
                    target="_blank"
                    rel="noopener"
                    data-track="contato"
                    data-label={`${s.ticket_url ? "Ingressos" : "Contato"} ${s.city}`}
                    className="mt-auto self-start rounded-full border border-champanhe/40 px-4 py-2 text-xs uppercase tracking-[0.2em] text-champanhe transition hover:bg-champanhe hover:text-noite"
                  >
                    {s.ticket_url ? "Ingressos" : "Contato"}
                  </a>
                </article>
              )}
            </li>
          );
        })}
      </ul>

      {/* Barra de tempo: a animação de 5 s É o temporizador do autoplay */}
      <div className="mt-6 flex items-center gap-4">
        {!expanded && (
          <div
            className="h-0.5 flex-1 overflow-hidden rounded-full bg-champanhe/10"
            aria-hidden
          >
            <div
              key={tick}
              className="h-full origin-left bg-champanhe/70"
              style={{
                animation: `shows-progress ${INTERVAL}ms linear both`,
                animationPlayState: playing ? "running" : "paused",
              }}
              onAnimationEnd={next}
            />
          </div>
        )}
        <button
          type="button"
          onClick={() => {
            setExpanded((x) => !x);
            if (expanded)
              rootRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
          }}
          className={`text-xs font-medium uppercase tracking-[0.25em] text-champanhe underline decoration-champanhe/40 underline-offset-4 transition hover:decoration-champanhe ${expanded ? "mx-auto" : ""}`}
        >
          {expanded ? "Voltar ao carrossel" : "Ver todas as datas"}
        </button>
      </div>
    </div>
  );
}
